import {
  AsyncCallArgs,
  BoxSpg,
  DividerSpg,
  GridContainer,
  GridItem,
  useAsyncCall,
  ButtonSpg,
  SmallFontAwesomeIcon,
  SecurityGuard,
  SecurityRole,
} from "@duosoftbg/nacid-components";
import { Alert, Button, Checkbox, FormControlLabel } from "@mui/material";
import EvaluationsFormSection from "./EvaluationsFormSection";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import DraftReceiptButton from "./DraftReceiptButton";
import {
  calculateFeesForId,
  evaluateApplication,
  fileApplicationForId,
  finalizeApplicationForId,
} from "../../../../../../services/serviceCalls";
import { handleFilingErrors } from "../../../../../../yup/utils/filingUtils";
import { useNavigate } from "react-router-dom";
import { faFileSignature } from "@fortawesome/free-solid-svg-icons";
import ApplicationFeesSection from "./ApplicationFeesSection";
import { createAppViewUrlWithBasePath, createMyApplicationsUrl } from "../../../../../../utils/applicationUrlUtils";
import { useAppDispatch } from "@duosoftbg/nacid-frontoffice-components";
import { enableAutoValidation } from "../../../../../../store/redux/slice/AutoServiceValidation/autoServiceValidation";

const FilingForm = ({
  basePath,
  appId,
  redirectPath = null,
  allowedFileApplicationAction = true,
  paymentConfirmAllowed = false,
}) => {
  const { t } = useTranslation();
  const { asyncCall } = useAsyncCall();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [evaluations, setEvaluations] = useState([]);
  const [fees, setFees] = useState({});
  const [feesState, setFeesState] = useState({ loading: true, error: false });
  const [validationErrors, setValidationErrors] = useState(null);
  const [isPaymentOnlineConfirmed, setPaymentOnlineConfirmed] = useState(null);

  useEffect(() => {
    dispatch(enableAutoValidation());
  }, [dispatch]);

  useEffect(() => {
    const calcFeesArgs: AsyncCallArgs = {
      promise: calculateFeesForId(basePath, appId),
      processResponseErrors: false,
      withGlobalBackdrop: false,
      onSuccess: (response) => {
        setFees(response.data);
        setFeesState({ loading: false, error: false });
      },
      onError: () => setFeesState({ loading: false, error: true }),
    };
    fillEvaluations();
    asyncCall(calcFeesArgs);
  }, [appId, basePath, setEvaluations, asyncCall]);

  const fillEvaluations = (isPaymentOnlineConfirmed = null) => {
    const evalArgs: AsyncCallArgs = {
      promise: evaluateApplication(basePath, appId, isPaymentOnlineConfirmed),
      processResponseErrors: false,
      withGlobalBackdrop: false,
      onSuccess: (response) => setEvaluations(response.data),
    };
    asyncCall(evalArgs);
  };
  const handlePaymentConfirmEvent = (event) => {
    const value = event.target.checked;
    setPaymentOnlineConfirmed(value);
    fillEvaluations(value);
  };

  const finalizeApplication = () => {
    const fileApplicationAsyncCall: AsyncCallArgs = {
      withGlobalBackdrop: true,
      promise: finalizeApplicationForId(basePath, appId),
      processResponseErrors: false,
      onSuccess: (response) => {
        window.location.replace(`${process.env.REACT_APP_SIGNATURE_URL}/sign-document/${response.data}`);
      },
      onError: (errResponse) => handleFilingErrors(errResponse, setValidationErrors),
    };
    asyncCall(fileApplicationAsyncCall);
  };

  const fileApplication = () => {
    const fileApplicationAsyncCall: AsyncCallArgs = {
      withGlobalBackdrop: true,
      promise: fileApplicationForId(basePath, appId),
      processResponseErrors: false,
      onSuccess: (response) => {
        if (response.data.length > 1) {
          navigate(createMyApplicationsUrl());
          toast.success(t("m.filing.application.multiple.success", { entryNumber: response.data.join(", ") }), {
            autoClose: 15000,
          });
        } else {
          navigate(createAppViewUrlWithBasePath(appId, redirectPath ? redirectPath : basePath));
          toast.success(t("m.filing.application.success", { entryNumber: response.data[0] }), { autoClose: 10000 });
        }
      },
      onError: (errResponse) => handleFilingErrors(errResponse, setValidationErrors),
    };
    asyncCall(fileApplicationAsyncCall);
  };

  const allEvaluationsPositive = () => {
    return evaluations.map((ev) => ev.evaluationValue).reduce((ev1, ev2) => ev1 && ev2);
  };

  if (evaluations.length === 0) {
    return (
      <BoxSpg mt={4}>
        <Alert severity={"info"}>{t("m.loading")}</Alert>
        <DividerSpg my={4} />
        <BoxSpg mt={10}></BoxSpg>
      </BoxSpg>
    );
  }
  return (
    <BoxSpg>
      <ApplicationFeesSection fees={fees} feesState={feesState} />
      {paymentConfirmAllowed && (
        <GridContainer>
          <GridItem style={{ marginLeft: "10px" }} xs={12} sm={12} md={12}>
            <FormControlLabel
              control={<Checkbox checked={isPaymentOnlineConfirmed} onChange={handlePaymentConfirmEvent} />}
              label={t("l.online.payment.confirmation")}
            />
          </GridItem>
        </GridContainer>
      )}
      <EvaluationsFormSection evaluations={evaluations} validationErrors={validationErrors} />
      <DividerSpg my={4} />
      <BoxSpg>
        {allEvaluationsPositive() ? (
          <>
            {allowedFileApplicationAction && (
              <Button variant={"contained"} onClick={fileApplication}>
                {t("l.btn.file")}
              </Button>
            )}
            {!allowedFileApplicationAction && (
              <SecurityGuard checkForRoles={[SecurityRole.FoEmployeeAppSubmit]}>
                <Button variant={"contained"} onClick={fileApplication}>
                  {t("l.btn.file")}
                </Button>
              </SecurityGuard>
            )}
            <ButtonSpg
              variant={"contained"}
              onClick={finalizeApplication}
              ml={3}
              startIcon={<SmallFontAwesomeIcon icon={faFileSignature} />}
            >
              {t("l.btn.finalize.sign")}
            </ButtonSpg>
            <DraftReceiptButton basePath={basePath} appId={appId} />
          </>
        ) : (
          <GridContainer mt={0}>
            <GridItem sm={6} md={6}>
              <Alert severity={"info"} sx={{ paddingTop: 0, paddingBottom: 0 }}>
                {t("m.correct.errors.to.submit")}
              </Alert>
            </GridItem>
            <GridItem sm={4} md={4}>
              <DraftReceiptButton basePath={basePath} appId={appId} />
            </GridItem>
          </GridContainer>
        )}
      </BoxSpg>
    </BoxSpg>
  );
};
export default FilingForm;
